import { cn } from "@/lib/utils";

/** Lightweight area line for TVL / debt history */
export function AreaChart({
	values,
	labels,
	className,
	colorClass = "text-primary",
}: {
	values: number[];
	labels?: string[];
	className?: string;
	colorClass?: string;
}) {
	if (values.length < 2) {
		return (
			<div
				className={cn(
					"flex h-36 items-center justify-center rounded-xl border border-dashed border-border text-xs text-muted-foreground",
					className,
				)}
			>
				Need ≥2 history points for a chart
			</div>
		);
	}
	const min = Math.min(...values);
	const max = Math.max(...values);
	const span = max - min || 1;
	const w = 320;
	const h = 120;
	const pad = 8;
	const pts = values.map((v, i) => {
		const x = pad + (i / (values.length - 1)) * (w - pad * 2);
		const y = h - pad - ((v - min) / span) * (h - pad * 2);
		return { x, y };
	});
	const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
	const area = `${pad},${h - pad} ${line} ${w - pad},${h - pad}`;

	return (
		<div className={cn("w-full", colorClass, className)}>
			<svg viewBox={`0 0 ${w} ${h}`} className="h-36 w-full" aria-hidden>
				<defs>
					<linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
						<stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
					</linearGradient>
				</defs>
				<polygon fill="url(#areaFill)" points={area} />
				<polyline
					fill="none"
					stroke="currentColor"
					strokeWidth="2.5"
					strokeLinejoin="round"
					strokeLinecap="round"
					points={line}
				/>
			</svg>
			{labels && labels.length >= 2 ? (
				<div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
					<span>{labels[0]}</span>
					<span>{labels[labels.length - 1]}</span>
				</div>
			) : null}
			<div className="mt-1 flex justify-between font-mono text-[10px] text-muted-foreground">
				<span>
					min{" "}
					{min.toLocaleString(undefined, { maximumFractionDigits: 0 })}
				</span>
				<span>
					max{" "}
					{max.toLocaleString(undefined, { maximumFractionDigits: 0 })}
				</span>
			</div>
		</div>
	);
}

/** Horizontal mix bar for collateral distribution */
export function MixBar({
	slices,
}: {
	slices: Array<{ asset: string; sharePct: number; color: string }>;
}) {
	const total = slices.reduce((a, s) => a + s.sharePct, 0) || 1;
	return (
		<div className="space-y-3">
			<div className="flex h-4 w-full overflow-hidden rounded-full bg-muted">
				{slices.map((s) => (
					<div
						key={s.asset}
						className="h-full transition-all"
						style={{
							width: `${(s.sharePct / total) * 100}%`,
							backgroundColor: s.color,
						}}
						title={`${s.asset} ${s.sharePct}%`}
					/>
				))}
			</div>
			<ul className="grid gap-2 sm:grid-cols-3">
				{slices.map((s) => (
					<li key={s.asset} className="flex items-center gap-2 text-sm">
						<span
							className="size-2.5 shrink-0 rounded-full"
							style={{ backgroundColor: s.color }}
						/>
						<span className="font-semibold">
							{s.asset}{" "}
							<span className="font-mono font-normal text-muted-foreground">
								({s.sharePct}%)
							</span>
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
